import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Loader2, CheckCheck, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminNotifications,
    fetchAdminNotificationsUnreadCount,
    markAdminNotificationRead,
    markAllAdminNotificationsRead,
} from "@/lib/api";

const POLL_MS = 30_000;

/**
 * Admin notification bell + slide-over panel.
 * Polls unread-count every 30s. Panel opens on click and loads full list.
 * Deep-links respect the notification's `deep_link` field.
 */
export default function AdminNotificationBell() {
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    const pollCount = useCallback(async () => {
        try {
            const r = await fetchAdminNotificationsUnreadCount();
            setCount(Number(r?.count || 0));
        } catch { /* silent — bell always visible */ }
    }, []);

    useEffect(() => {
        pollCount();
        timerRef.current = setInterval(pollCount, POLL_MS);
        return () => timerRef.current && clearInterval(timerRef.current);
    }, [pollCount]);

    const loadPanel = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAdminNotifications({ limit: 50 });
            setItems(res.items || []);
            setCount(res.total_unread || 0);
        } catch {
            toast.error("Kunne ikke hente notifikationer");
        } finally {
            setLoading(false);
        }
    }, []);

    const toggle = () => {
        setOpen((o) => {
            const next = !o;
            if (next) loadPanel();
            return next;
        });
    };

    const onClickItem = async (item) => {
        try {
            if (!item.read_at) {
                await markAdminNotificationRead(item.id);
                setCount((c) => Math.max(0, c - 1));
                setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, read_at: new Date().toISOString() } : x));
            }
        } catch { /* non-blocking */ }
        if (item.deep_link) {
            setOpen(false);
            navigate(item.deep_link);
        }
    };

    const onMarkAll = async () => {
        setBusy(true);
        try {
            const r = await markAllAdminNotificationsRead();
            setCount(0);
            setItems((prev) => prev.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
            toast.success(`${r?.count || 0} markeret som læst`);
        } catch {
            toast.error("Kunne ikke markere alle som læst");
        } finally { setBusy(false); }
    };

    return (
        <>
            <button
                type="button"
                onClick={toggle}
                data-testid="admin-notification-bell"
                className="relative w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center transition"
                aria-label={`Notifikationer (${count} ulæste)`}
            >
                <Bell className="w-4 h-4 text-slate-600" />
                {count > 0 && (
                    <span
                        data-testid="admin-notification-badge"
                        className="absolute top-0 right-0 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-sm"
                    >
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>

            {open && (
                <>
                    <div
                        className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] z-40"
                        onClick={() => setOpen(false)}
                        data-testid="admin-notification-backdrop"
                    />
                    <aside
                        className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
                        data-testid="admin-notification-panel"
                    >
                        <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-bold text-slate-900">Notifikationer</h3>
                                <p className="text-xs text-slate-500">
                                    {count} ulæst{count === 1 ? "" : "e"} · på tværs af alle virksomheder
                                </p>
                            </div>
                            <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded" data-testid="admin-notification-close">
                                <X className="w-4 h-4 text-slate-500" />
                            </button>
                        </header>
                        <div className="px-5 py-2 border-b border-slate-100 flex justify-end">
                            <button
                                type="button"
                                onClick={onMarkAll}
                                disabled={busy || count === 0}
                                data-testid="admin-notification-read-all"
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 transition"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />}
                                Markér alle som læst
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                </div>
                            ) : items.length === 0 ? (
                                <div className="text-center py-16 text-sm text-slate-400" data-testid="admin-notification-empty">
                                    Ingen notifikationer at vise.
                                </div>
                            ) : (
                                <ul className="divide-y divide-slate-100">
                                    {items.map((n) => (
                                        <NotificationRow key={n.id} n={n} onClick={() => onClickItem(n)} />
                                    ))}
                                </ul>
                            )}
                        </div>
                    </aside>
                </>
            )}
        </>
    );
}

function NotificationRow({ n, onClick }) {
    const unread = !n.read_at;
    const sev = (n.severity || "info").toLowerCase();
    const iconCls = sev === "critical" ? "text-rose-600" : sev === "warning" ? "text-amber-600" : "text-blue-600";
    const Icon = sev === "critical" ? AlertCircle : sev === "warning" ? AlertTriangle : Info;
    const t = n.created_at ? new Date(n.created_at) : null;
    const timeAgo = t ? formatAgo(t) : "";
    return (
        <li
            data-testid={`admin-notification-row-${n.id}`}
            onClick={onClick}
            className={`px-5 py-3 cursor-pointer transition ${unread ? "bg-blue-50/40 hover:bg-blue-50/70" : "hover:bg-slate-50"}`}
        >
            <div className="flex items-start gap-3">
                <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${iconCls}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        {unread && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                        <p className={`text-sm truncate ${unread ? "font-semibold text-slate-900" : "text-slate-700"}`}>{n.title}</p>
                    </div>
                    {n.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>}
                    <div className="flex items-center gap-2 mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                        <span>{n.company_name || "—"}</span>
                        <span>·</span>
                        <span>{timeAgo}</span>
                    </div>
                </div>
            </div>
        </li>
    );
}

function formatAgo(d) {
    const diffMs = Date.now() - d.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "lige nu";
    if (min < 60) return `${min} min. siden`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} t. siden`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days} dag${days === 1 ? "" : "e"} siden`;
    return d.toLocaleDateString("da-DK", { day: "2-digit", month: "short" });
}
